<script setup>
import { t, message, schemaText } from '../lib/i18n.mjs'
import { reactive, ref, computed } from 'vue'
import ModalShell from './ModalShell.vue'
import AppIcon from './AppIcon.vue'
import MotifPreview from './MotifPreview.vue'
const props = defineProps({
  kind: String,
  item: Object,
  schemas: Array,
  links: Array,
  facets: Array,
  usage: { type: Number, default: 0 },
})
const emit = defineEmits(['close', 'save', 'delete'])
const deleting = ref(false)
const replacement = ref('')
const deletable = computed(() => props.item && ['node', 'link', 'facet'].includes(props.kind))
const alternatives = computed(() =>
  (props.kind === 'node' ? props.schemas : props.links).filter((item) => item.id !== props.item?.id),
)
function remove() {
  if (props.kind === 'facet') return emit('delete')
  if (!alternatives.value.length) return
  if (props.usage && !deleting.value) {
    deleting.value = true
    return
  }
  if (props.usage && !alternatives.value.some((item) => item.id === replacement.value)) return
  emit('delete', replacement.value)
}
const draft = reactive(
  JSON.parse(
    JSON.stringify(
      props.item || {
        id: '',
        name: '',
        english: '',
        definition: '',
        color: '#8cbdb5',
        dash: 'solid',
        terms: '',
        kind: 'attribute',
        extraction: 'llm',
        llmEnabled: false,
        nodeTypes: {},
        nodeFacets: {},
        edgeTypes: [],
        nodes: 3,
        edges: [
          [2, 1],
          [3, 1],
        ],
        mode: 'any',
        linkType: '',
      },
    ),
  ),
)
if (props.kind === 'facet') {
  draft.kind = 'attribute'
  draft.extraction ||= ['person', 'location', 'time'].includes(draft.id) ? 'script' : 'dictionary'
}
if (props.kind === 'motif') {
  draft.nodeTypes ||= {}
  draft.nodeFacets ||= {}
  draft.edgeTypes ||= []
  for (let n = 1; n <= 6; n++) draft.nodeFacets[n] ||= { id: '', value: '' }
}
const error = ref('')
const title = computed(
  () =>
    `${props.item ? '编辑' : '新建'}${{ node: '节点类型', link: '关系类型', facet: '实体类别', motif: '拓扑' }[props.kind]}`,
)
function removeEdge(index) {
  draft.edges.splice(index, 1)
  draft.edgeTypes.splice(index, 1)
}
function changeNodes() {
  const keep = draft.edges
    .map((edge, index) => ({ edge, type: draft.edgeTypes[index] }))
    .filter(({ edge: [a, b] }) => a <= draft.nodes && b <= draft.nodes)
  draft.edges = keep.map((x) => x.edge)
  draft.edgeTypes = keep.map((x) => x.type || '')
  for (const key of Object.keys(draft.nodeTypes)) if (Number(key) > draft.nodes) delete draft.nodeTypes[key]
  for (let n = 1; n <= draft.nodes; n++) draft.nodeFacets[n] ||= { id: '', value: '' }
}
function save() {
  if (deleting.value) return remove()
  error.value = ''
  if (!draft.name.trim()) {
    error.value = '请填写名称。'
    return
  }
  if (props.kind === 'motif') {
    const edges = draft.edges
    if (
      !edges.length ||
      edges.some(([a, b]) => a === b) ||
      new Set(edges.map((e) => e.join('-'))).size !== edges.length
    ) {
      error.value = '请添加有效关系，避免自环与重复边。'
      return
    }
    const visited = new Set([1])
    for (let i = 0; i < draft.nodes; i++)
      edges.forEach(([a, b]) => {
        if (visited.has(a) || visited.has(b)) {
          visited.add(a)
          visited.add(b)
        }
      })
    if (visited.size !== draft.nodes) {
      error.value = '所有节点需连成一个拓扑。'
      return
    }
  } else if (!draft.definition.trim()) {
    error.value = '请填写定义。'
    return
  }
  if (['node', 'link'].includes(props.kind) && !draft.english.trim()) {
    error.value = '请填写英文名。'
    return
  }
  if (['node', 'link'].includes(props.kind)) {
    const list = props.kind === 'node' ? props.schemas : props.links
    draft.id ||= Math.max(0, ...list.map((item) => Number(item.id) || 0)) + 1
  } else draft.id ||= `${props.kind}_${Date.now().toString(36)}`
  const output = JSON.parse(JSON.stringify(draft))
  if (props.kind === 'facet' && !props.item) output.llmEnabled = true
  if (props.kind === 'motif') {
    for (const key of Object.keys(output.nodeTypes))
      if (Number(key) > output.nodes || !output.nodeTypes[key]) delete output.nodeTypes[key]
    for (const key of Object.keys(output.nodeFacets))
      if (Number(key) > output.nodes || !output.nodeFacets[key]?.id) delete output.nodeFacets[key]
  }
  emit('save', output)
}
</script>
<template>
  <ModalShell
    :title="t(title)"
    :eyebrow="t({ node: '节点类型', link: '关系类型', facet: '实体类别', motif: '拓扑' }[kind])"
    @close="emit('close')"
  >
    <form class="editor-form" @submit.prevent="save">
      <template v-if="kind === 'motif'">
        <label
          >{{ t('拓扑名称')
          }}<input
            :value="schemaText(draft, 'name', kind)"
            @input="draft.name = $event.target.value"
            required
            maxlength="48"
        /></label>
        <label
          >{{ t('定义')
          }}<textarea
            :value="schemaText(draft, 'definition', kind)"
            @input="draft.definition = $event.target.value"
            rows="2"
            :placeholder="t('描述这种论证模式的含义')"
          />
        </label>
        <div class="form-columns">
          <label
            >{{ t('节点数')
            }}<input
              v-model.number="draft.nodes"
              type="number"
              min="2"
              max="6"
              required
              @change="changeNodes" /></label
          ><label
            >{{ t('关系类型')
            }}<select v-model="draft.linkType">
              <option value="">{{ t('不限') }}</option>
              <option v-for="link in links" :key="link.id" :value="link.id">
                {{ schemaText(link, 'name', 'link') }}
              </option>
            </select></label
          >
        </div>
        <div class="motif-node-constraints">
          <div v-for="n in draft.nodes" :key="n" class="form-columns">
            <label
              >{{ t('节点') }} {{ n }} · {{ t('类型') }}
              <select v-model="draft.nodeTypes[n]">
                <option value="">{{ t('不限') }}</option>
                <option v-for="node in schemas" :key="node.id" :value="node.id">
                  {{ schemaText(node, 'name', 'node') }}
                </option>
              </select>
            </label>
            <label
              >{{ t('分析切面') }}
              <select v-model="draft.nodeFacets[n].id">
                <option value="">{{ t('不限') }}</option>
                <option v-for="facet in facets" :key="facet.id" :value="facet.id">
                  {{ schemaText(facet, 'name', 'facet') }}
                </option>
              </select>
              <input
                v-if="draft.nodeFacets[n].id"
                v-model="draft.nodeFacets[n].value"
                :placeholder="t('属性值，留空表示存在该属性')"
              />
            </label>
          </div>
        </div>
        <div class="topology-editor">
          <MotifPreview :motif="draft" />
          <div class="topology-edges">
            <div v-for="(edge, i) in draft.edges" :key="i" class="edge-row">
              <select v-model.number="edge[0]" :aria-label="t('关系 {0} 起点', { 0: i + 1 })">
                <option v-for="n in draft.nodes" :key="n">{{ n }}</option></select
              ><AppIcon name="ArrowRight" /><select
                v-model.number="edge[1]"
                :aria-label="t('关系 {0} 终点', { 0: i + 1 })"
              >
                <option v-for="n in draft.nodes" :key="n">{{ n }}</option></select
              ><select v-model="draft.edgeTypes[i]" :aria-label="t('关系 {0} 类型', { 0: i + 1 })">
                <option value="">{{ t('不限') }}</option>
                <option v-for="link in links" :key="link.id" :value="link.id">
                  {{ schemaText(link, 'name', 'link') }}
                </option></select
              ><button
                type="button"
                class="icon-button"
                :title="t('删除边')"
                :aria-label="t('删除边')"
                @click="removeEdge(i)"
              >
                <AppIcon name="Trash2" :size="14" />
              </button>
            </div>
            <button type="button" class="subtle-button" @click="draft.edges.push([2, 1])">
              <AppIcon name="Plus" :size="14" />{{ t('添加边') }}
            </button>
          </div>
        </div>
        <label
          >{{ t('支撑方式')
          }}<select v-model="draft.mode">
            <option value="any">{{ t('不限') }}</option>
            <option value="single">{{ t('独立单边论证') }}</option>
            <option value="joint">{{ t('联合支撑') }}</option>
            <option value="independent">{{ t('独立支撑') }}</option>
            <option v-if="draft.mode === 'example'" value="example">{{ t('例证支撑') }}</option>
          </select></label
        >
      </template>
      <template v-else>
        <div class="form-columns">
          <label
            >{{ kind === 'facet' ? t('类别名称') : t('中文名')
            }}<input
              :value="schemaText(draft, 'name', kind)"
              @input="draft.name = $event.target.value"
              required
              maxlength="32" /></label
          ><label v-if="kind !== 'facet'"
            >{{ t('英文名') }}<input v-model="draft.english" required maxlength="40"
          /></label>
        </div>
        <label
          >{{ t('定义')
          }}<textarea
            :value="schemaText(draft, 'definition', kind)"
            @input="draft.definition = $event.target.value"
            rows="3"
            required
          />
        </label>
        <template v-if="kind === 'facet'">
          <label
            >{{ t('提取方式') }}
            <select
              :aria-label="t('提取方式')"
              v-model="draft.extraction"
              :disabled="['person', 'location', 'time'].includes(draft.id)"
            >
              <option v-if="['person', 'location', 'time'].includes(draft.id)" value="script">
                {{ t('本地实体脚本') }}
              </option>
              <option value="llm">{{ t('Facet Agent · 按定义提取属性') }}</option>
              <option value="dictionary">{{ t('词表脚本 · 精确匹配') }}</option>
            </select>
          </label>
          <label v-if="draft.extraction !== 'llm'"
            >{{ t('提取词表') }}<input v-model="draft.terms" :placeholder="t('多个词以逗号分隔')"
          /></label>
          <p class="form-hint">
            {{
              !item && draft.extraction === 'llm'
                ? t('添加后提取各单元的属性并汇总到段落。')
                : t('保存后点击“更新 Facet”应用修改。')
            }}
          </p>
        </template>
        <div v-else class="form-columns">
          <label
            >{{ t('颜色') }}
            <div class="color-field">
              <input v-model="draft.color" type="color" :aria-label="t('类型颜色')" /><input
                v-model="draft.color"
                :aria-label="t('十六进制颜色')"
                pattern="#[a-fA-F0-9]{6}"
                required
              /></div></label
          ><label v-if="kind === 'link'"
            >{{ t('线型')
            }}<select v-model="draft.dash">
              <option value="solid">{{ t('实线') }}</option>
              <option value="dashed">{{ t('虚线') }}</option>
              <option value="dotted">{{ t('点线') }}</option>
            </select></label
          >
        </div>
      </template>
      <p v-if="error" class="form-error" role="alert">{{ message(error) }}</p>
      <div v-if="deleting" class="schema-delete-panel">
        <p>
          {{
            t('工作区中有 {count} 处引用此分类。请选择替代分类，原有节点、边和引用内容会保留。', {
              count: usage,
            })
          }}
        </p>
        <label
          >{{ t('替代分类')
          }}<select v-model="replacement">
            <option disabled value="">{{ t('请选择分类') }}</option>
            <option v-for="option in alternatives" :key="option.id" :value="option.id">
              {{ schemaText(option, 'name', kind) }}
            </option>
          </select></label
        >
      </div>
      <p v-if="deletable && kind === 'facet'" class="form-hint">
        {{ t('仅删除当前文章的此属性、提取结果及相关 Motif 条件，其他文章不受影响。') }}
      </p>
      <p v-if="deletable && kind !== 'facet' && !alternatives.length" class="form-hint">
        {{
          t('至少保留一个{kind}类型；可先新增分类再删除。', { kind: t(kind === 'node' ? '节点' : '关系') })
        }}
      </p>
      <footer class="modal-actions">
        <button
          v-if="deletable"
          type="button"
          class="danger-button schema-delete-button"
          :disabled="kind !== 'facet' && (!alternatives.length || (deleting && !replacement))"
          @click="remove"
        >
          <AppIcon name="Trash2" :size="15" />{{
            kind === 'facet' ? t('删除属性') : deleting ? t('替换并删除') : t('删除分类')
          }}
        </button>
        <button type="button" class="secondary-button" @click="emit('close')">{{ t('取消') }}</button
        ><button v-if="!deleting" class="primary-button" type="submit">
          <AppIcon name="Check" :size="15" />{{ t('保存') }}
        </button>
      </footer>
    </form>
  </ModalShell>
</template>
